import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/utils/classname';

/**
 * SelectionBanner provides a Gmail-style two-step "select all matching" UX.
 *
 * UX Flow:
 * 1. User checks header checkbox → all loaded rows selected
 * 2. If `isAllLoadedSelected && hasNextPage`, banner shows:
 *    "All X on this page selected. Select all matching assets?"
 * 3. User clicks "Select all matching" → `onSelectAllMatching()`
 * 4. In `all` mode: "All matching assets selected. Clear selection"
 *
 * Note: The banner deliberately does NOT show a specific count for "all matching".
 * The exact count appears in the job result after completion.
 *
 * @example
 * ```tsx
 * <SelectionBanner
 *   selectedCount={selectedRows.length}
 *   totalLoadedCount={data.length}
 *   hasNextPage={hasNextPage}
 *   isSelectAll={isSelectAll}
 *   isAllLoadedSelected={isAllLoadedSelected}
 *   resourceLabel="assets"
 *   onSelectAllMatching={selectAllMatching}
 *   onClearSelection={clearSelection}
 * />
 * ```
 */
export interface SelectionBannerProps {
  /** Number of items currently selected */
  selectedCount: number;
  /** Total items loaded in the table */
  totalLoadedCount: number;
  /** Whether more data exists beyond loaded pages */
  hasNextPage?: boolean;
  /** Whether we are in 'all' mode (select all matching) */
  isSelectAll?: boolean;
  /** True when all loaded rows are selected (triggers "Select all?" prompt) */
  isAllLoadedSelected?: boolean;
  /** Resource label for display (e.g. "assets", "vulnerabilities") */
  resourceLabel: string;
  /** Callback to promote to 'all' mode */
  onSelectAllMatching: () => void;
  /** Callback to clear selection */
  onClearSelection: () => void;
  /**
   * Whether to show the "Select all matching?" prompt.
   * Only enable when the section has full bulk endpoint wiring
   * (useBulkAction + BulkConfirmationDialog + shouldUseBulk).
   * Defaults to false — sections without bulk wiring must NOT set this to true.
   */
  enableSelectAllMatching?: boolean;
  /** Optional CSS class override */
  className?: string;
}

/**
 * SelectionBanner — Gmail two-step bulk selection banner.
 */
export const SelectionBanner = React.memo<SelectionBannerProps>(
  function SelectionBanner({
    selectedCount,
    totalLoadedCount,
    hasNextPage = false,
    isSelectAll = false,
    isAllLoadedSelected = false,
    resourceLabel,
    onSelectAllMatching,
    onClearSelection,
    enableSelectAllMatching = false,
    className,
  }) {
    const isVisible = isAllLoadedSelected || isSelectAll;

    return (
      <AnimatePresence mode="wait">
        {isVisible && (
          <motion.div
            key="selection-banner"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className={cn('overflow-hidden', className)}
            data-testid="selection-banner"
          >
            <div
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className="border-chariot-stroke bg-chariot-background-secondary mb-4 flex items-center justify-between gap-4 rounded-md border px-4 py-3"
            >
              {/* Left side: Icon + Message */}
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <InformationCircleIcon
                  className="text-chariot-icon-secondary size-5 shrink-0"
                  aria-hidden="true"
                />
                <span className="text-chariot-body-s text-chariot-text-secondary min-w-0">
                  {isSelectAll ? (
                    <>
                      All matching{' '}
                      <span className="font-semibold">{resourceLabel}</span>{' '}
                      selected.
                    </>
                  ) : enableSelectAllMatching &&
                    isAllLoadedSelected &&
                    hasNextPage ? (
                    <>
                      Only {selectedCount} {resourceLabel} on this page are
                      selected.{' '}
                      <button
                        type="button"
                        onClick={onSelectAllMatching}
                        aria-label={`Select all matching ${resourceLabel}`}
                        className="text-chariot-brand-primary hover:text-chariot-brand-secondary focus-visible:outline-brand-500 font-semibold underline focus-visible:outline-2 focus-visible:outline-offset-2"
                        data-testid="select-all-matching-btn"
                      >
                        Select all matching {resourceLabel}?
                      </button>
                    </>
                  ) : (
                    <>
                      {selectedCount} of {totalLoadedCount} {resourceLabel}{' '}
                      selected.
                    </>
                  )}
                </span>
              </div>

              {/* Right side: Clear button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearSelection}
                data-testid="clear-selection-btn"
                className="shrink-0"
              >
                <XMarkIcon className="size-4" aria-hidden="true" />
                Clear
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }
);
